/**
 * API Client for Admin UI
 *
 * Provides type-safe API calls to the gateway backend.
 */

import type {
  ApiResponse,
  ApiError,
  DashboardSummary,
  TrafficDataPoint,
  RecentInterception,
  LogsResponse,
  LogDetail,
  ModerationRule,
  CreateRuleRequest,
  TestRuleResult,
  TestAiRuleResult,
  UnifiedTestResult,
  ClientToken,
  GenerateTokenResponse,
  SystemSettings,
  Metrics,
  HealthStatus,
  VersionInfo,
} from '../types';

// ==========================================
// Configuration
// ==========================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// ==========================================
// Authentication State
// ==========================================

// Note: Authentication is now handled via HttpOnly cookies set by the backend.
// We no longer store tokens in localStorage for security reasons.
// The browser automatically includes the cookie in requests when credentials: 'include' is set.

let isAuthenticatedState = false;

export function setAuthenticationState(authenticated: boolean): void {
  isAuthenticatedState = authenticated;
}

export function isAuthenticated(): boolean {
  return isAuthenticatedState;
}

// ==========================================
// Base Request Function
// ==========================================

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Include cookies in cross-origin requests
  });

  if (!response.ok) {
    const errorData: ApiError = await response.json().catch(() => ({
      error: {
        message: `HTTP ${response.status}: ${response.statusText}`,
        type: 'http_error',
        code: String(response.status),
      },
    }));

    throw new Error(errorData.error.message);
  }

  return response.json();
}

// ==========================================
// Authentication API
// ==========================================

export interface LoginRequest {
  token: string;
}

export interface LoginResponse {
  success: boolean;
  user: {
    id: number;
    name: string;
    type: string;
  };
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export async function login(token: string): Promise<LoginResponse> {
  const response = await request<ApiResponse<LoginResponse>>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return response.data;
}

export async function logout(): Promise<LogoutResponse> {
  const response = await request<ApiResponse<LogoutResponse>>('/api/auth/logout', {
    method: 'POST',
  });
  return response.data;
}

export async function getCurrentUser(): Promise<LoginResponse['user']> {
  const response = await request<ApiResponse<{ user: LoginResponse['user'] }>>('/api/auth/me');
  return response.data.user;
}

// ==========================================
// Health Check API
// ==========================================

export async function getHealth(): Promise<HealthStatus> {
  return request<HealthStatus>('/health');
}

// ==========================================
// Version API
// ==========================================

export async function getVersion(): Promise<VersionInfo> {
  const response = await request<ApiResponse<VersionInfo>>('/api/version');
  return response.data;
}

// ==========================================
// Dashboard API
// ==========================================

export async function getDashboardSummary(
  hours: number = 24
): Promise<DashboardSummary> {
  const response = await request<ApiResponse<DashboardSummary>>(
    `/api/dashboard/summary?hours=${hours}`
  );
  return response.data;
}

export async function getTrafficData(
  hours: number = 24,
  interval: number = 60
): Promise<TrafficDataPoint[]> {
  const response = await request<ApiResponse<TrafficDataPoint[]>>(
    `/api/dashboard/traffic?hours=${hours}&interval=${interval}`
  );
  return response.data;
}

export async function getRecentInterceptions(
  limit: number = 5
): Promise<RecentInterception[]> {
  const response = await request<ApiResponse<RecentInterception[]>>(
    `/api/dashboard/recent-interceptions?limit=${limit}`
  );
  return response.data;
}

// ==========================================
// Logs API
// ==========================================

export interface GetLogsParams {
  search?: string;
  status?: string;
  model?: string;
  clientId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  includeContent?: boolean; // Include request/response bodies (for export)
}

export async function getLogs(params: GetLogsParams = {}): Promise<LogsResponse> {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.set(key, String(value));
    }
  });

  const response = await request<ApiResponse<LogsResponse>>(
    `/api/logs?${searchParams.toString()}`
  );
  return response.data;
}

export async function getLogDetail(requestId: string): Promise<LogDetail> {
  const response = await request<ApiResponse<LogDetail>>(
    `/api/logs/${requestId}`
  );
  return response.data;
}

export async function getLogModels(): Promise<string[]> {
  const response = await request<ApiResponse<{ models: string[] }>>(
    '/api/logs/models'
  );
  return response.data.models;
}

export async function getLogClients(): Promise<Array<{ id: number; name: string }>> {
  const response = await request<ApiResponse<{ clients: Array<{ id: number; name: string }> }>>(
    '/api/logs/clients'
  );
  return response.data.clients;
}

// ==========================================
// Rules API
// ==========================================

export async function getRules(params: {
  active?: boolean;
  category?: string;
} = {}): Promise<ModerationRule[]> {
  const searchParams = new URLSearchParams();

  if (params.active !== undefined) {
    searchParams.set('active', String(params.active));
  }
  if (params.category) {
    searchParams.set('category', params.category);
  }

  const response = await request<ApiResponse<{ rules: ModerationRule[] }>>(
    `/api/rules?${searchParams.toString()}`
  );
  return response.data.rules;
}

export async function createRule(data: CreateRuleRequest): Promise<ModerationRule> {
  const response = await request<ApiResponse<{ rule: ModerationRule }>>(
    '/api/rules',
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.data.rule;
}

export async function updateRule(
  id: number,
  data: Partial<CreateRuleRequest>
): Promise<ModerationRule> {
  const response = await request<ApiResponse<{ rule: ModerationRule }>>(
    `/api/rules/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  return response.data.rule;
}

export async function deleteRule(id: number): Promise<void> {
  await request<ApiResponse<{ success: boolean }>>(`/api/rules/${id}`, {
    method: 'DELETE',
  });
}

export async function testRule(
  pattern: string,
  testString: string
): Promise<TestRuleResult> {
  const response = await request<ApiResponse<TestRuleResult>>('/api/rules/test', {
    method: 'POST',
    body: JSON.stringify({ pattern, testString }),
  });
  return response.data;
}

export async function testAiRule(
  testString: string,
  upstreamId: number,
  model: string,
  prompt: string,
  threshold: number,
  timeout?: number
): Promise<TestAiRuleResult> {
  const response = await request<ApiResponse<TestAiRuleResult>>('/api/rules/test-ai', {
    method: 'POST',
    body: JSON.stringify({ testString, upstreamId, model, prompt, threshold, timeout }),
  });
  return response.data;
}

export async function testUnifiedRule(params: {
  testString: string;
  moderationType: 'regex' | 'ai' | 'hybrid';
  pattern?: string;
  upstreamId?: number;
  model?: string;
  prompt?: string;
  threshold?: number;
  timeout?: number;
}): Promise<UnifiedTestResult> {
  const response = await request<ApiResponse<UnifiedTestResult>>('/api/rules/test-unified', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return response.data;
}

// ==========================================
// Clients API
// ==========================================

export async function getClients(status?: string): Promise<ClientToken[]> {
  const searchParams = new URLSearchParams();
  if (status) {
    searchParams.set('status', status);
  }

  const response = await request<ApiResponse<{ tokens: ClientToken[] }>>(
    `/api/clients?${searchParams.toString()}`
  );
  return response.data.tokens;
}

export async function generateClient(
  name: string,
  type: 'client' | 'admin' = 'client'
): Promise<GenerateTokenResponse> {
  const response = await request<ApiResponse<GenerateTokenResponse>>(
    '/api/clients/generate',
    {
      method: 'POST',
      body: JSON.stringify({ name, type }),
    }
  );
  return response.data;
}

export async function updateClient(
  id: number,
  data: {
    name?: string;
    status?: string;
    rateLimitEnabled?: boolean;
    rateLimitMaxRequests?: number;
    rateLimitWindowMs?: number;
  }
): Promise<ClientToken> {
  const response = await request<ApiResponse<{ token: ClientToken }>>(
    `/api/clients/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  return response.data.token;
}

export async function revokeClient(id: number): Promise<void> {
  await request<ApiResponse<{ success: boolean }>>(`/api/clients/${id}`, {
    method: 'DELETE',
  });
}

// ==========================================
// Settings API
// ==========================================

export async function getSettings(): Promise<{ settings: SystemSettings }> {
  const response = await request<
    ApiResponse<{ settings: SystemSettings; raw: any[] }>
  >('/api/settings');
  return response.data;
}

export async function updateSettings(
  data: Partial<SystemSettings>
): Promise<SystemSettings> {
  const response = await request<ApiResponse<{ settings: SystemSettings }>>(
    '/api/settings',
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  return response.data.settings;
}

export async function getMetrics(): Promise<Metrics> {
  const response = await request<ApiResponse<{ metrics: Metrics }>>(
    '/api/metrics'
  );
  return response.data.metrics;
}

// ==========================================
// Generic HTTP Methods
// ==========================================

export async function get<T = any>(endpoint: string): Promise<T> {
  return request<T>(`/api${endpoint}`);
}

export async function post<T = any>(endpoint: string, data?: any): Promise<T> {
  return request<T>(`/api${endpoint}`, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function put<T = any>(endpoint: string, data?: any): Promise<T> {
  return request<T>(`/api${endpoint}`, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function del<T = any>(endpoint: string): Promise<T> {
  return request<T>(`/api${endpoint}`, {
    method: 'DELETE',
  });
}

// ==========================================
// Export API object
// ==========================================

export const api = {
  // Auth
  login,
  logout,
  getCurrentUser,
  setAuthenticationState,
  isAuthenticated,

  // Health
  getHealth,

  // Version
  getVersion,

  // Dashboard
  getDashboardSummary,
  getTrafficData,
  getRecentInterceptions,

  // Logs
  getLogs,
  getLogDetail,
  getLogModels,
  getLogClients,

  // Rules
  getRules,
  createRule,
  updateRule,
  deleteRule,
  testRule,
  testAiRule,
  testUnifiedRule,

  // Clients
  getClients,
  generateClient,
  updateClient,
  revokeClient,

  // Settings
  getSettings,
  updateSettings,
  getMetrics,

  // Generic HTTP methods (for dynamic API calls)
  get,
  post,
  put,
  delete: del,
};

export default api;

